import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { MapDocument } from "../lib/mapDocument";
import {
  buildStoryboardMapLocationIndex,
  type StoryboardMapLocationChip,
} from "../lib/storyboardMapLocations";
import type { UpdateFn } from "../lib/tea";

export type TimelineMapIndex = Map<string, StoryboardMapLocationChip[]>;

/** One parsed `.zwmap` plus the asset bytes its previews need. */
export type CachedTimelineMap = {
  readonly name: string;
  readonly doc: MapDocument;
  readonly assets: ReadonlyMap<string, string>;
};

export type Model = {
  root: Maybe<string>;
  maps: readonly CachedTimelineMap[];
  index: TimelineMapIndex;
  ready: boolean;
  /** Last full read failed. The timeline still opens; the next load may retry. */
  failed: boolean;
  loading: boolean;
  generation: number;
  /** Maps written while a full read was in flight. `Loaded` keeps these. */
  savedDuringLoad: ReadonlySet<string>;
  /** Maps deleted while a full read was in flight. `Loaded` drops these. */
  removedDuringLoad: ReadonlySet<string>;
};

export const initialModel: Model = {
  root: nothing,
  maps: [],
  index: new Map(),
  ready: false,
  failed: false,
  loading: false,
  generation: 0,
  savedDuringLoad: new Set(),
  removedDuringLoad: new Set(),
};

export type Msg =
  | { type: "Load"; root: Maybe<string> }
  | { type: "Loaded"; generation: number; maps: readonly CachedTimelineMap[] }
  | { type: "Failed"; generation: number }
  | { type: "MapUpdated"; map: CachedTimelineMap }
  | { type: "MapRemoved"; name: string }
  | { type: "MapRenamed"; from: string; to: string };

export type Cmd = { root: string; generation: number };

function upsertMap(
  maps: readonly CachedTimelineMap[],
  next: CachedTimelineMap,
): readonly CachedTimelineMap[] {
  return [...maps.filter((map) => map.name !== next.name), next];
}

function indexOf(maps: readonly CachedTimelineMap[]): TimelineMapIndex {
  return buildStoryboardMapLocationIndex(
    maps.map((map) => ({
      name: map.name,
      doc: map.doc,
      assets: just(map.assets),
    })),
  );
}

export const update: UpdateFn<Model, Msg, Cmd> = (model, msg) => {
  switch (msg.type) {
    case "Load": {
      const sameRoot =
        isJust(model.root) &&
        isJust(msg.root) &&
        model.root.value === msg.root.value;
      if (isNothing(msg.root)) {
        return [{ ...initialModel, generation: model.generation + 1 }, []];
      }
      if (sameRoot && model.loading) return [model, []];
      // Opening the timeline again uses the index already built for this project.
      if (sameRoot && model.ready && model.failed === false) return [model, []];
      const generation = model.generation + 1;
      return [
        {
          ...model,
          root: msg.root,
          generation,
          loading: true,
          failed: false,
          ready: false,
          maps: sameRoot ? model.maps : [],
          index: sameRoot ? model.index : new Map(),
          savedDuringLoad: new Set(),
          removedDuringLoad: new Set(),
        },
        [{ root: msg.root.value, generation }],
      ];
    }
    case "Loaded": {
      if (msg.generation !== model.generation) return [model, []];
      const removed = model.removedDuringLoad;
      const overrides = model.maps.filter(
        (map) =>
          model.savedDuringLoad.has(map.name) &&
          removed.has(map.name) === false,
      );
      const overrideNames = new Set(overrides.map((map) => map.name));
      const maps = [
        ...msg.maps.filter(
          (map) =>
            removed.has(map.name) === false &&
            overrideNames.has(map.name) === false,
        ),
        ...overrides,
      ];
      return [
        {
          ...model,
          maps,
          index: indexOf(maps),
          loading: false,
          ready: true,
          failed: false,
          savedDuringLoad: new Set(),
          removedDuringLoad: new Set(),
        },
        [],
      ];
    }
    case "Failed":
      if (msg.generation !== model.generation) return [model, []];
      return [{ ...model, loading: false, ready: true, failed: true }, []];
    case "MapUpdated": {
      if (isNothing(model.root)) return [model, []];
      const maps = upsertMap(model.maps, msg.map);
      if (model.loading) {
        const savedDuringLoad = new Set(model.savedDuringLoad);
        savedDuringLoad.add(msg.map.name);
        const removedDuringLoad = new Set(model.removedDuringLoad);
        removedDuringLoad.delete(msg.map.name);
        return [{ ...model, maps, savedDuringLoad, removedDuringLoad }, []];
      }
      return [
        {
          ...model,
          maps,
          index: indexOf(maps),
          ready: true,
          failed: false,
        },
        [],
      ];
    }
    case "MapRemoved": {
      const maps = model.maps.filter((map) => map.name !== msg.name);
      if (model.loading) {
        const removedDuringLoad = new Set(model.removedDuringLoad);
        removedDuringLoad.add(msg.name);
        const savedDuringLoad = new Set(model.savedDuringLoad);
        savedDuringLoad.delete(msg.name);
        return [{ ...model, maps, removedDuringLoad, savedDuringLoad }, []];
      }
      return [{ ...model, maps, index: indexOf(maps) }, []];
    }
    case "MapRenamed": {
      const present = model.maps.some((map) => map.name === msg.from);
      if (present === false) return [model, []];
      const maps = model.maps.map((map) =>
        map.name === msg.from ? { ...map, name: msg.to } : map,
      );
      if (model.loading) {
        const savedDuringLoad = new Set(model.savedDuringLoad);
        if (savedDuringLoad.has(msg.from)) {
          savedDuringLoad.delete(msg.from);
          savedDuringLoad.add(msg.to);
        }
        const removedDuringLoad = new Set(model.removedDuringLoad);
        if (removedDuringLoad.has(msg.from)) {
          removedDuringLoad.delete(msg.from);
          removedDuringLoad.add(msg.to);
        }
        return [{ ...model, maps, savedDuringLoad, removedDuringLoad }, []];
      }
      return [{ ...model, maps, index: indexOf(maps) }, []];
    }
    default:
      return assertNever(msg);
  }
};
